/**
 * **Abgabezettel: was Server und Vorschlagsfenster gleich rechnen müssen** (AP-22 Teil 6, V).
 *
 * Die KI schreibt den Zettel ab, der Server macht daraus je Tag einen Vorschlag
 * (`apps/backend/src/modules/payroll/slip-read.ts`). Im Vorschlagsfenster passt der Mensch
 * Werte an, bevor er übernimmt — und was er dabei sieht (die Pause, die den Zeiten folgt; ob der
 * Tag schon so im Raster steht), muss dasselbe sein, was der Server beim Übernehmen schreibt.
 * Darum liegt diese Rechnung im Kern: eine Regel, zwei Benutzer.
 *
 * Rein — keine Datenbank, kein Netz, kein Wissen über die KI.
 */

/** Felder einer Tageszeile, wie der Zettel sie vorschlägt. */
export interface ZettelFelder {
  startTime?: string;
  endTime?: string;
  breakMinutes?: number;
  travelHours?: number;
  departure?: string;
  isDriver?: boolean;
  overnight?: boolean;
  absenceTypeId?: string;
  absenceScope?: 'FULL' | 'HALF';
  /** Fehlzeit in Stunden, wenn der Zettel sie nennt — gilt dann statt Art und Umfang. */
  absences?: { absenceTypeId: string; hours: number }[];
  note?: string;
}

/**
 * Was der Mensch am Vorschlag eines Tages ändert: je Feld der neue Wert; `null` nimmt das Feld
 * aus dem Vorschlag (es wird dann nicht übernommen).
 */
export type ZettelAnpassung = { [F in keyof ZettelFelder]?: ZettelFelder[F] | null };

export const ZETTEL_FELDER: readonly (keyof ZettelFelder)[] = [
  'startTime',
  'endTime',
  'breakMinutes',
  'travelHours',
  'departure',
  'isDriver',
  'overnight',
  'absenceTypeId',
  'absenceScope',
  'absences',
  'note',
];

/** Wie ein vorgeschlagener Tag zur vorhandenen Tageszeile steht. */
export type ZettelLage = 'NEW' | 'SAME' | 'DIFFERENT';

const HOCH: Record<string, string> = { '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9' };

/** Hochgestellte Ziffern werden Nachkomma- bzw. Minutenstellen: „18³⁰" → „18 30". */
export function zettelText(text: string): string {
  return text.replace(/([⁰¹²³⁴-⁹]+)/g, (m) => ` ${[...m].map((z) => HOCH[z]).join('')}`).trim();
}

/** Eine Dezimalzahl mit Komma, Punkt oder hochgestellten Nachkommastellen; „158 50" ist 158,50. */
export function leseZahlText(roh: string | null | undefined): number | null {
  if (roh === null || roh === undefined) return null;
  const t = zettelText(String(roh))
    .toLowerCase()
    .replace(/(std|h|tage?|x)\.?/g, '')
    .trim();
  const m = /^(-?\d{1,4})(?:\s*[.,\s]\s*(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const zahl = Number(`${m[1]}.${m[2] ?? '0'}`);
  return Number.isFinite(zahl) ? zahl : null;
}

function minuten(zeit: string): number {
  return Number(zeit.slice(0, 2)) * 60 + Number(zeit.slice(3));
}

/**
 * **Pause = (bis − von) − Gesamtarbeitsstunden** — der Zettel hat keine
 * Pausenspalte. „7,15" bei 06:15–13:30 meint 7 h 15 min: Passt die dezimale
 * Lesart nicht in ein 5-Minuten-Raster, die Lesart „Stunden,Minuten" aber in
 * ein Viertelstunden-Raster, gilt diese — und die Pause ist unsicher.
 */
export function pauseAus(von: string, bis: string, stundenRoh: string | null): { minuten: number | null; unsicher: boolean; hinweis?: string } {
  const spanne = (bis === '23:59' ? 1440 : minuten(bis)) - minuten(von);
  const dezimal = leseZahlText(stundenRoh);
  if (dezimal === null) return { minuten: null, unsicher: true, hinweis: 'Gesamtarbeitsstunden nicht lesbar — die Pause folgt der Regel aus den Einstellungen.' };
  if (spanne <= 0) return { minuten: null, unsicher: true, hinweis: 'Geht liegt nicht nach Kommt.' };
  const pauseDezimal = Math.round(spanne - dezimal * 60);
  const nachkomma = /[.,\s](\d{2})\s*$/.exec(zettelText(String(stundenRoh)).trim())?.[1];
  const hm = nachkomma !== undefined && Number(nachkomma) < 60 ? Math.trunc(dezimal) * 60 + Number(nachkomma) : null;
  const pauseHm = hm === null ? null : spanne - hm;
  const imRaster = (p: number, raster: number) => p >= 0 && p <= 240 && p % raster === 0;
  if (imRaster(pauseDezimal, 5)) return { minuten: pauseDezimal, unsicher: false };
  if (pauseHm !== null && imRaster(pauseHm, 15)) {
    return { minuten: pauseHm, unsicher: true, hinweis: `„${stundenRoh}" als Stunden und Minuten gelesen.` };
  }
  if (pauseDezimal >= 0 && pauseDezimal <= 240) {
    return { minuten: pauseDezimal, unsicher: true, hinweis: 'Die Pause aus der Differenz liegt in keinem Fünf-Minuten-Raster.' };
  }
  return {
    minuten: null,
    unsicher: true,
    hinweis: `Gesamtarbeitsstunden (${stundenRoh}) passen nicht zu ${von}–${bis} — die Pause folgt der Regel aus den Einstellungen.`,
  };
}

/**
 * Der Vorschlag eines Tages gegen die vorhandene Tageszeile: neu, steht schon so da, oder weicht
 * ab — verglichen wird nur, was der Vorschlag trägt. `vorhanden` sind die Felder der Zeile
 * (`null`, wo sie nichts trägt); ohne Zeile ist der Tag neu.
 */
export function zettelLage(felder: ZettelFelder, vorhanden: Record<string, unknown> | null | undefined): { lage: ZettelLage; abweichend: string[] } {
  if (!vorhanden) return { lage: 'NEW', abweichend: [] };
  const abweichend = (Object.keys(felder) as (keyof ZettelFelder)[]).filter((f) => {
    const ist = vorhanden[f];
    const soll = felder[f];
    if (Array.isArray(soll)) return JSON.stringify(ist ?? null) !== JSON.stringify(soll);
    return typeof soll === 'number' ? Number(ist ?? NaN) !== soll : (ist ?? null) !== soll;
  });
  return { lage: abweichend.length ? 'DIFFERENT' : 'SAME', abweichend };
}

/** Was `mitAnpassung` von einem Tag des Vorschlags braucht. */
export interface ZettelTagBasis {
  felder: ZettelFelder;
  /** Felder, die unsicher gelesen wurden. */
  unsicher: string[];
  hinweise: string[];
  /** Je Hinweis das Feld, zu dem er gehört — gleiche Reihenfolge wie `hinweise`. */
  hinweisFeld: string[];
  /** `stundenText`: die Gesamtarbeitsstunden, wie sie auf dem Zettel stehen — daraus folgt die Pause. */
  kontrolle: { stundenText?: string | null };
  /** Das Wort für die Fehlzeit, wenn keine Art dazu passt. */
  fehlzeitWort: string | null;
}

export interface ZettelAngepasst {
  /** Felder, die nach der Anpassung anders sind als gelesen — auch, was ihr folgt (die Pause den Zeiten). */
  angepasst: string[];
  /** Was an diesen Feldern gelesen worden war (`null`: nichts). */
  gelesen: Record<string, unknown>;
}

const FEHLZEIT: readonly string[] = ['absenceTypeId', 'absenceScope', 'absences'];

function leer(w: unknown): boolean {
  return w === null || w === undefined || w === '' || w === false || (Array.isArray(w) && w.length === 0);
}

function gleich(a: unknown, b: unknown): boolean {
  return JSON.stringify(leer(a) ? null : a) === JSON.stringify(leer(b) ? null : b);
}

/**
 * Den Vorschlag eines Tages mit dem, was der Mensch daran geändert hat.
 *
 * - Ein genanntes Feld gilt; `null` nimmt es heraus.
 * - **Die Pause folgt den Zeiten**: Sind Kommt oder Geht geändert und die Pause nicht selbst
 *   genannt, wird sie neu aus der Differenz zu den Gesamtarbeitsstunden des Zettels gerechnet —
 *   dieselbe Regel wie beim Lesen (`pauseAus`), mit ihrem Hinweis.
 * - Fehlzeit in Stunden gilt statt Art und Umfang; eine Art ohne Umfang ist ein ganzer Tag,
 *   neben Arbeitszeit ein halber.
 * - Was der Mensch angefasst hat, ist nicht mehr „unsicher gelesen", und der Hinweis dazu entfällt.
 */
export function mitAnpassung<T extends ZettelTagBasis>(tag: T, anpassung: ZettelAnpassung | null | undefined): T & ZettelAngepasst {
  const a = (anpassung ?? {}) as Record<string, unknown>;
  const genannt = ZETTEL_FELDER.filter((f) => a[f] !== undefined);
  if (genannt.length === 0) return { ...tag, angepasst: [], gelesen: {} };

  const alt = tag.felder as Record<string, unknown>;
  const felder: Record<string, unknown> = { ...alt };
  for (const f of genannt) {
    if (leer(a[f])) delete felder[f];
    else felder[f] = a[f];
  }

  if (!leer(felder.absences)) {
    delete felder.absenceTypeId;
    delete felder.absenceScope;
  } else {
    delete felder.absences;
    if (!felder.absenceTypeId) delete felder.absenceScope;
    else if (!felder.absenceScope) felder.absenceScope = felder.startTime && felder.endTime ? 'HALF' : 'FULL';
  }

  let pause: ReturnType<typeof pauseAus> | null = null;
  const zeitAnders = !gleich(felder.startTime, alt.startTime) || !gleich(felder.endTime, alt.endTime);
  if (zeitAnders && !genannt.includes('breakMinutes')) {
    pause =
      felder.startTime && felder.endTime
        ? pauseAus(String(felder.startTime), String(felder.endTime), tag.kontrolle.stundenText ?? null)
        : { minuten: null, unsicher: false };
    if (pause.minuten !== null) felder.breakMinutes = pause.minuten;
    else delete felder.breakMinutes;
  }

  const angepasst = ZETTEL_FELDER.filter((f) => !gleich(felder[f], alt[f])) as string[];
  if (angepasst.length === 0) return { ...tag, angepasst: [], gelesen: {} };

  const beruehrt = new Set<string>(angepasst);
  if (angepasst.some((f) => FEHLZEIT.includes(f))) for (const f of FEHLZEIT) beruehrt.add(f);
  if (pause) beruehrt.add('breakMinutes');

  const unsicher = tag.unsicher.filter((f) => !beruehrt.has(f));
  const hinweise: string[] = [];
  const hinweisFeld: string[] = [];
  tag.hinweise.forEach((h, i) => {
    if (beruehrt.has(tag.hinweisFeld[i])) return;
    hinweise.push(h);
    hinweisFeld.push(tag.hinweisFeld[i]);
  });
  if (pause?.unsicher) unsicher.push('breakMinutes');
  if (pause?.hinweis) {
    hinweise.push(pause.hinweis);
    hinweisFeld.push('breakMinutes');
  }

  return {
    ...tag,
    felder: felder as ZettelFelder,
    unsicher,
    hinweise,
    hinweisFeld,
    fehlzeitWort: felder.absenceTypeId || felder.absences ? null : tag.fehlzeitWort,
    angepasst,
    gelesen: Object.fromEntries(angepasst.map((f) => [f, alt[f] ?? null])),
  };
}
